// Traducciones de valores del backend a español argentino.
// Se usa en badges, estados, feeds, etc. Centralizado para mantener consistencia.

export const SEVERITY_LABEL = {
  info: "Info",
  warning: "Atención",
  critical: "Crítica",
};

export const SEVERITY_COLOR = {
  info: "green",
  warning: "amber",
  critical: "red",
};

// Semáforo de prioridad para Novedades (clasificación por IA + corrección humana).
export const PRIORITY_LABEL = {
  critical: "ALTA",
  warning: "MEDIA",
  info: "BAJA",
};

export const PRIORITY_RANK = {
  critical: 3,
  warning: 2,
  info: 1,
};

export const SERVICE_STATUS_LABEL = {
  open: "Abierto",
  closed: "Cerrado",
};

export const SCAN_STATUS_LABEL = {
  ok: "Correcto",
  fuera_de_orden: "Fuera de orden",
  manual: "Manual",
};

export const SCAN_STATUS_COLOR = {
  ok: "green",
  fuera_de_orden: "red",
  manual: "amber",
};

export const PATROL_STATUS_LABEL = {
  pendiente: "Pendiente",
  en_curso: "En curso",
  completada: "Completada",
  incompleta: "Incompleta",
  cancelada: "Cancelada",
  vencida: "Vencida (no realizada)",
  no_realizada: "No realizada (vencida, histórico)",
};

export const PATROL_STATUS_COLOR = {
  pendiente: "gray",
  en_curso: "blue",
  completada: "green",
  incompleta: "red",
  cancelada: "gray",
  vencida: "red",
  no_realizada: "red",
};

// Cómo se puede marcar un punto de ronda.
export const VERIFICATION_MODE_LABEL = {
  qr: "QR",
  gps: "Cercanía GPS",
  ambos: "QR o GPS",
};

export const SCAN_METHOD_LABEL = {
  qr: "QR",
  gps: "Cercanía GPS",
};

// Una ronda pendiente que ya pasó su horario programado + tolerancia nunca
// llegó a iniciarse: se la considera "vencida / no realizada", distinta de
// "en curso" (ya arrancó) y "completada" (ya se hizo). El status persistido
// en la base sigue siendo "pendiente" — esto es un cálculo en vivo.
export function isPatrolOverdue(p) {
  if (!p || p.status !== "pendiente" || !p.scheduled_for) return false;
  const sched = new Date(p.scheduled_for).getTime();
  const tolMs = (p.tolerance_minutes ?? 60) * 60000;
  return Date.now() > sched + tolMs;
}

// Fix AW (2026-09-29): fecha y hora SIEMPRE en 24 hs y en hora argentina, sin depender de la
// configuración del dispositivo (el selector nativo de fecha y hora muestra "a. m./p. m." en
// muchas computadoras). Argentina es UTC−3 todo el año.
const AR_TZ = "America/Argentina/Buenos_Aires";
export function formatDateTimeAR(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("es-AR", { timeZone: AR_TZ, hourCycle: "h23", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
export function formatTimeAR(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("es-AR", { timeZone: AR_TZ, hourCycle: "h23", hour: "2-digit", minute: "2-digit" });
}
// "YYYY-MM-DD" y "HH:MM" en hora argentina, para cargar los selectores.
export function splitDateTimeAR(iso) {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: AR_TZ, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(iso));
  const g = (t) => (parts.find((x) => x.type === t) || {}).value;
  return { date: `${g("year")}-${g("month")}-${g("day")}`, time: `${g("hour")}:${g("minute")}` };
}
// Fecha "YYYY-MM-DD" + hora "HH:MM" argentinas -> ISO.
export function isoFromARDateTime(date, time) {
  return new Date(`${date}T${time}:00-03:00`).toISOString();
}

export const MOVEMENT_TYPE_LABEL = {
  jornada_normal: "Jornada normal",
  espera_relevo: "Espera de relevo",
  cobertura_parcial: "Cobertura parcial",
};

// Fix Z (2026-09-14): saldo de horas extra (pago o devolución).
export const SETTLEMENT_STATUS_LABEL = {
  pendiente: "Pendiente",
  pagada: "Pagada",
  devuelta: "Devuelta",
};

export const PANIC_STATUS_LABEL = {
  pendiente: "Pendiente",
  en_atencion: "En atención",
  resuelta: "Resuelta",
};

export const REQUEST_STATUS_LABEL = {
  pendiente: "Pendiente",
  aprobada: "Aprobada",
  rechazada: "Rechazada",
};

// Fix AA (2026-09-17): mapa completo de tipos de solicitud PARA MOSTRAR
// (incluye "adelanto" a propósito, para que las solicitudes históricas de
// ese tipo se sigan viendo con su etiqueta). El desplegable de alta usa una
// lista aparte, sin "adelanto" -- ver REQUEST_TYPES en cada formulario.
export const REQUEST_TYPE_LABEL = {
  adelanto: "Adelanto de sueldo",
  material: "Pedido de material",
  refuerzo_compra: "Refuerzo / compras",
  cambio_personal: "Cambio de personal",
  otro: "Otro",
};

export const FEED_TYPE_LABEL = {
  news: "Novedad",
  service: "Servicio",
  scan: "Ronda",
  patrol: "Ronda",
};

// Helper genérico: dada una clave (status/severity) devuelve label legible.
// Si no la conoce, capitaliza la primera letra y reemplaza guiones bajos.
export function prettify(value) {
  if (!value) return "—";
  const all = { ...SEVERITY_LABEL, ...SERVICE_STATUS_LABEL, ...SCAN_STATUS_LABEL,
                 ...PATROL_STATUS_LABEL, ...PANIC_STATUS_LABEL };
  if (all[value]) return all[value];
  const s = String(value).replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Saludo contextual según hora local de Mendoza, Argentina (UTC-3).
export function saludoMendoza(now = new Date()) {
  // Mendoza no usa DST → UTC-3 fijo.
  const mzaHour = (now.getUTCHours() - 3 + 24) % 24;
  if (mzaHour < 6) return "Buenas noches";
  if (mzaHour < 13) return "Buen día";
  if (mzaHour < 20) return "Buenas tardes";
  return "Buenas noches";
}

// Reloj formateado HH:MM en zona Mendoza (UTC-3).
export function relojMendoza(now = new Date()) {
  const off = -3 * 60; // minutos
  const local = new Date(now.getTime() + (now.getTimezoneOffset() + off) * 60000);
  const hh = String(local.getHours()).padStart(2, "0");
  const mm = String(local.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

// Fecha formateada larga para Mendoza: "Lunes 9 de febrero, 2026"
export function fechaLargaMendoza(now = new Date()) {
  const off = -3 * 60;
  const local = new Date(now.getTime() + (now.getTimezoneOffset() + off) * 60000);
  const dias = ["Domingo","Lunes","Martes","Miércoles","Jueves","Viernes","Sábado"];
  const meses = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
  return `${dias[local.getDay()]} ${local.getDate()} de ${meses[local.getMonth()]}, ${local.getFullYear()}`;
}
